import { supabase } from './supabase'

/**
 * The bin, and the way back out of it.
 *
 * Everything here goes through database functions rather than an update on the
 * row, for two reasons. Deleting an obligation has to take its instalments
 * with it or the payments list keeps showing a payment for a thing that no
 * longer exists — and that consistency belongs in one place, not in every
 * caller. And a deleted row is invisible to the row-level policies that list
 * things, so the trash itself can only be read by a function that does its own
 * authorisation.
 *
 * Rule 7: nothing here removes anything. A nightly job does that, thirty days
 * later.
 */

export type TrashKind = 'document' | 'obligation' | 'task'

export interface TrashItem {
  kind: TrashKind
  id: string
  spaceId: string
  title: string
  deletedAt: string
  /** The day it stops being recoverable. */
  purgesOn: string
}

export async function loadTrash(): Promise<TrashItem[]> {
  const { data, error } = await supabase.rpc('trash_contents')
  if (error) throw error

  return (data ?? []).map((row: Record<string, unknown>) => ({
    kind: row.kind as TrashKind,
    id: row.id as string,
    spaceId: row.space_id as string,
    title: (row.title as string) || 'Zonder naam',
    deletedAt: row.deleted_at as string,
    purgesOn: row.purges_on as string,
  }))
}

export async function moveToTrash(kind: TrashKind, id: string): Promise<void> {
  const { error } = await supabase.rpc('move_to_trash', { p_kind: kind, p_id: id })
  if (error) throw new Error(friendly(error.message))
}

export async function restoreFromTrash(kind: TrashKind, id: string): Promise<void> {
  const { error } = await supabase.rpc('restore_from_trash', { p_kind: kind, p_id: id })
  if (error) throw new Error(friendly(error.message))
}

/**
 * Database errors are not sentences a person should have to read.
 *
 * The retention guard in particular deserves a real explanation, because
 * "computer says no" on a delete button is exactly the moment someone decides
 * the app is fighting them. It is not refusing to be difficult; it is refusing
 * to lose a record of money that moved.
 */
function friendly(message: string): string {
  if (message.includes('al op betaald')) {
    return 'Hier is al op betaald, dus dit blijft bewaard. Wil je het uit je lijst? Gebruik Annuleren op de betaling.'
  }
  if (message.includes('not allowed')) return 'Je mag dit niet verwijderen.'
  if (message.includes('not found') || message.includes('not in the trash')) {
    return 'Dit bestaat niet meer.'
  }
  return 'Verwijderen lukte niet.'
}

export function describeDaysLeft(purgesOn: string, today = new Date()): string {
  const midnight = new Date(today)
  midnight.setHours(0, 0, 0, 0)
  const days = Math.round(
    (new Date(`${purgesOn}T00:00:00`).getTime() - midnight.getTime()) / 86400000,
  )
  if (days <= 0) return 'Wordt vandaag definitief verwijderd'
  if (days === 1) return 'Nog 1 dag terug te halen'
  return `Nog ${days} dagen terug te halen`
}

export function describeKind(kind: TrashKind): string {
  return { document: 'Document', obligation: 'Betaling', task: 'Brief' }[kind]
}
